import Calendar from '@/components/calendar/Calendar';
import { Form, FormControl, FormField, FormItem } from '@/components/common/Form';
import { Label } from '@/components/common/Label';
import { ROUTES } from '@/configs/constants';
import { useStore } from '@/hooks/useStore';
import yup from '@/services/yup';
import { formatDateTime } from '@/utils/datetime';
import { formatNumber } from '@/utils/utils';
import { yupResolver } from '@hookform/resolvers/yup';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { flowResult } from 'mobx';
import { observer } from 'mobx-react-lite';
import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

interface ViewDetailReferralCodeProps {
    id: string | number,
    startTimeProp: Date | null | undefined,
    endTimeProp: Date | null | undefined
}

export default observer(function ViewDetailReferralCodeModal({ id, startTimeProp, endTimeProp }: ViewDetailReferralCodeProps) {
    // hooks
    const { t, i18n: { language } } = useTranslation();
    const navigate = useNavigate();

    // validate Schema
    const validateSchema = yup.object().shape({
        startTime: yup.date().nullable(),
        endTime: yup.date().nullable()
    });

    type FormData = yup.InferType<typeof validateSchema>;

    const initialValues = {
        startTime: null,
        endTime: null
    };

    const form = useForm<FormData>({
        resolver: yupResolver(validateSchema),
        mode: 'onChange',
        defaultValues: initialValues
    });

    const watchStartTime = form.watch('startTime');
    const watchEndTime = form.watch('endTime');

    // store
    const {
        referralCodeStore: { referralCodeDetail, getReferralCodeDetail },
        modalStore: { hideModal, showAlertModal }
    } = useStore();

    // state: chỉ giữ pageNumber để gọi API
    const [pageNumber, setPageNumber] = useState(1);
    const pageSize = 10;

    // fetch API
    const fetchData = async () => {
        if (id) {
            const payload = {
                size: pageSize,
                page: pageNumber,
                startTime: watchStartTime,
                endTime: watchEndTime
            };
            await flowResult(getReferralCodeDetail(payload, id));
        }
    };

    useEffect(() => {
        form.reset({
            startTime: startTimeProp,
            endTime: endTimeProp
        });
    }, [startTimeProp, endTimeProp]);

    // lifecycle
    useEffect(() => {
        fetchData();
    }, [id, pageNumber, watchStartTime, watchEndTime]);

    return (
        <div className='pb-4 space-y-4'>
            <div>
                <div className='flex'>
                    <div className='mr-2 text-gray-500 font-medium text-[14px] whitespace-nowrap'>{t('words_title.referral_detail_referral_code')}</div>
                    <div className='font-semibold'>{referralCodeDetail?.detail?.code}</div>
                </div>
                <div className='flex mt-2'>
                    <div className='mr-2 text-gray-500 font-medium text-[14px]  whitespace-nowrap'>{t('words_title.referral_detail_organization_name')}</div>
                    <div className='font-semibold'>{referralCodeDetail?.detail?.organizationName}</div>
                </div>
            </div>

            <div className='flex items-center justify-between border-b pb-2 mb-4 mt-0'>
                <div className='text-[#9A6051] font-semibold text-[16px]'>
                    {t('words_title.referral_detail_list_accounts')}
                    {' '}
                    (
                    {referralCodeDetail?.pagination?.totalRecord ?? 0}
                    )
                </div>
                <div>
                    <Form {...form}>
                        <form>
                            <div className='flex items-start'>
                                <Label className='w-1/5 label-1 h-[48px] leading-[48px]'>{t('words_title.referral_detail_period')}</Label>
                                <div className='flex items-center gap-3 w-4/5'>
                                    <FormField
                                        control={form.control}
                                        name='startTime'
                                        render={({ field }) => (
                                            <FormItem className='w-[170px]'>
                                                <FormControl>
                                                    <Calendar
                                                        mode='single'
                                                        selected={field.value}
                                                        onSelect={field.onChange}
                                                        useIconRemove={true}
                                                        className='w-full'
                                                        placeholder={t('placeholder.from')}
                                                        required
                                                    />
                                                </FormControl>
                                            </FormItem>
                                        )}
                                    />
                                    <div> - </div>
                                    <FormField
                                        control={form.control}
                                        name='endTime'
                                        render={({ field }) => (
                                            <FormItem className='w-[170px]'>
                                                <FormControl>
                                                    <Calendar
                                                        mode='single'
                                                        selected={field.value}
                                                        onSelect={field.onChange}
                                                        useIconRemove={true}
                                                        className='w-full'
                                                        placeholder={t('placeholder.to')}
                                                        required
                                                    />
                                                </FormControl>
                                            </FormItem>
                                        )}
                                    />
                                </div>
                            </div>
                        </form>
                    </Form>
                </div>
            </div>

            {/* Table */}
            <div className='max-h-[400px] overflow-y-auto overflow-x-auto rounded-lg shadow'>
                <table className='min-w-full text-sm text-left border-collapse'>
                    <thead className='bg-gray-50 sticky top-0 z-10'>
                        <tr>
                            <th className='px-4 py-3 font-semibold text-gray-900'>{t('words_title.referral_detail_table_full_name')}</th>
                            <th className='px-4 py-3 font-semibold text-gray-900'>{t('words_title.referral_detail_table_phone_number')}</th>
                            <th className='px-4 py-3 font-semibold text-gray-900'>{t('words_title.referral_detail_table_application_date')}</th>
                        </tr>
                    </thead>
                    <tbody className='divide-y divide-gray-200'>
                        {(referralCodeDetail?.elements?.length > 0) ?
                            (
                                <>
                                    {referralCodeDetail?.elements?.map((row: any, index: number) => (
                                        <tr
                                            key={index}
                                            className={row.isDelete ? 'text-gray-400' : ''}
                                        >
                                            <td className='px-4 py-3 max-w-[450px]'>
                                                {row.isDelete ?
                                                    (
                                                        <>
                                                            {row?.fullName}
                                                            {' '}
                                                            <span className='text-red-500'>{t('words_title.deleted')}</span>
                                                        </>
                                                    ) :
                                                    (
                                                        <span
                                                            className='text-blue-600 hover:underline cursor-pointer'
                                                            onClick={() => {
                                                                navigate(`${ROUTES.userManagement.href}/${row.code}`);
                                                                hideModal();
                                                            }}
                                                        >
                                                            {row?.fullName}
                                                        </span>
                                                    )}
                                            </td>
                                            <td className='px-4 py-3 w-[180px]'>{row?.phoneNumber}</td>
                                            <td className='px-4 py-3 w-[200px]'>{row?.timeUsed ? formatDateTime(row?.timeUsed, 'DD/MM/YYYY  hh:mm A') : '--'}</td>
                                        </tr>
                                    ))}
                                </>
                            ) :
                            (
                                <tr>
                                    <td colSpan={3} className='px-4 py-10 text-center text-gray-500'>
                                        {t('words_title.no_data_referral')}
                                    </td>
                                </tr>
                            )}
                    </tbody>
                </table>
            </div>

            {/* Info + Pagination */}
            {
                (referralCodeDetail?.pagination && referralCodeDetail?.pagination?.totalRecord > 0) && (
                    <div className='flex items-center justify-between'>
                        <p className='text-sm text-gray-600'>
                            {t('table.paging_info', {
                                from: (referralCodeDetail.pagination.page - 1) * referralCodeDetail.pagination.size + 1,
                                to: Math.min(referralCodeDetail.pagination.page * referralCodeDetail.pagination.size, referralCodeDetail.pagination.totalRecord),
                                total: formatNumber(referralCodeDetail.pagination.totalRecord ?? 0)
                            })}

                        </p>

                        <div className='flex items-center space-x-2'>
                            {/* Prev */}
                            <button
                                className={`px-4 py-2 text-sm disabled:opacity-50 
                                ${referralCodeDetail.pagination.page === 1 ? '' : 'hover:bg-[#F1F5F9] rounded-sm'}`}
                                onClick={() => setPageNumber(p => Math.max(p - 1, 1))}
                                disabled={referralCodeDetail.pagination.page === 1}
                            >
                                <div className='flex items-center'>
                                    <ChevronLeft />
                                    {t('button.previous')}
                                </div>
                            </button>

                            {/* Page numbers */}
                            {[...Array(referralCodeDetail.pagination.totalPage)].map((_, i) => {
                                const current = referralCodeDetail.pagination.page;
                                const page = i + 1;

                                if (
                                    page === 1 ||
                                    page === referralCodeDetail.pagination.totalPage ||
                                    (page >= current - 1 && page <= current + 1)
                                ) {
                                    return (
                                        <button
                                            key={i}
                                            className={`px-4 py-2 text-sm rounded-lg transition-colors duration-200  ${current === page ?
                                                'border border-[#9A6051] text-[#9A6051] hover:bg-[#9A6051] hover:text-white' :
                                                'text-gray-700 border-gray-300 hover:bg-gray-100'}`}
                                            onClick={() => setPageNumber(page)}
                                        >
                                            {page}
                                        </button>
                                    );
                                } else if (page === current - 2 || page === current + 2) {
                                    return (
                                        <span key={i} className='px-2 text-gray-400'>
                                            ...
                                        </span>
                                    );
                                }

                                return null;
                            })}

                            {/* Next */}
                            <button
                                className={`px-4 py-2 text-sm disabled:opacity-50 
                                ${referralCodeDetail.pagination.page === referralCodeDetail.pagination.totalPage ? '' : 'hover:bg-[#F1F5F9] rounded-sm'}`}
                                onClick={() =>
                                    setPageNumber(p =>
                                        Math.min(
                                            p + 1,
                                            referralCodeDetail.pagination.totalPage
                                        ))}
                                disabled={
                                    referralCodeDetail.pagination.page ===
                                    referralCodeDetail.pagination.totalPage
                                }
                            >
                                <div className='flex items-center'>
                                    {t('button.next')}
                                    <ChevronRight />
                                </div>
                            </button>
                        </div>
                    </div>
                )
            }
        </div>
    );
});
